/**
 * GeneratePanel (spec 017 US2 + P1 background-job rewire) — the studio-plan
 * CUSTOMER "Generate" screen.
 *
 * A SIMPLIFIED, NO-PROMPT version of the admin Wizard generate flow: the customer
 * uploads one to five case references, then picks from admin-defined dropdowns (resolved
 * option groups). There is NO free-text prompt anywhere — the server composes the
 * trusted harness from the selected option choice ids.
 *
 * The option group with kind==="axis" is the Views axis (multi-select). Clicking
 * Generate ENQUEUES one server-side job per selected view (`client.enqueueGeneration`,
 * one call per view) and returns IMMEDIATELY — it never awaits generation. A polling
 * hook (`useGenerationJobs`) then refreshes each job's status on an interval until
 * every job is terminal, driving a per-view status list (Queued / Generating… / Done /
 * Failed). Completed jobs expose their image via a signed `resultUrl` (pixels live in
 * R2, never in the API). "Continue" fetches the chosen result's bytes and opens the template
 * wizard's Mask step on them (A2): automatic magenta key, the Details prefilled, Publish.
 *
 * What the server sends the provider, in this FIXED order (named by the prompt): the
 * CASE references (one required, up to five — any photo that contains the case), the PRODUCT reference
 * (optional — the real phone) and the VIEW reference (the chosen view's own reference
 * image, never uploaded here; it is the view chip's thumbnail). Brand and model are
 * required: they name the real phone the image is grounded in.
 *
 * The tab opens on the INDEX of the owner's generations (from the cloud, newest first), each
 * with how far it got: Generated → In template → Published. "New generation" opens the setup
 * form; Generate keeps the form on screen, its Generations column showing the new jobs, which
 * also go on top of the index. Coming back from another tab shows the index.
 */
import { useGuideSafeUrl } from "./whiteGuide.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServices } from "../app/context.js";
import { InfoHint } from "../ui/Tooltip.js";
import { Modal } from "../ui/Modal.js";
import { ActionBar } from "../ui/ActionBar.js";
import { EmptyState } from "../ui/EmptyState.js";
import { ViewPicker } from "./ViewPicker.js";
import { ProductPickers } from "../wizard/ProductPickers.js";
import { BaseTypePicker, StylePicker } from "../wizard/DetailsPickers.js";
import { usePhoneCatalog } from "../wizard/usePhoneCatalog.js";
import { Icon } from "../ui/Icon.js";
import {
  fetchResultBytes,
  toDataUri,
  guideMissing,
  hasImage,
  isReady,
  isTerminal,
  jobBadge,
  useDocumentVisible,
  jobLabel,
  useGenerationJobs,
  useObjectUrl,
} from "./candidates.js";
import { useExpiredLinkRefresh, useGenerationIndex } from "./generationIndex.js";
import { JobRow } from "./JobRow.js";
import { RunRow } from "./RunRow.js";
import { RunGallery } from "./RunGallery.js";
import {
  groupRuns, latestPerView, mockupOnlyRun, runCounts, runCountsText, runKey, runTemplateId, viewLabel, type Run,
} from "./runs.js";
import { confirmAction } from "../ui/confirmAction.js";
import { higgsfieldMcp } from "@genz/platform";
import { mayGenerateOnThisComputer, resolveGenerationSource, thisComputerMayGenerate } from "../stores/generationSource.js";
import { useSessionStore } from "../stores/session.js";
import { PaidCollectError, abandonPaidDeviceRun, clearStuckDeviceRun, isStuckOnUnknownSubmission, runDeviceGeneration, storageAvailable, unsettledDeviceRuns, unsettledRunError } from "./runDeviceGeneration.js";
import type { GenerationJobView, GenerationOutput, GenerationPrintArea, ProductTemplate, ResolvedOptionGroup, TemplateOptions } from "@genz/platform";
import { MAX_CASE_REFERENCES, dropPublishedJobs, useGenerateDraft, type DraftDetails, type DraftImage, type DraftJob } from "../stores/generateDraft.js";
import {
  generationJobIds, openWizardOnGeneration, openWizardOnRun, useWizardStore, wizardView, type GeneratedImage, type RunView,
} from "../stores/wizard.js";
import { Wizard } from "../wizard/Wizard.js";
import { TemplateEditor, type RunViewIntoTemplate } from "../templates/TemplateEditor.js";
import { notifyCatalogChanged } from "../templates/catalogInvalidation.js";
import { PRODUCT_CATEGORIES } from "../templates/catalogOptions.js";
import { modelLabel } from "../templates/templateLabels.js";
import "./generate.css";
import { RemovableImage } from "../ui/RemovableImage.js";
import { errorDetail } from "./errorCode.js";
import { ErrorNotice } from "../ui/ErrorNotice.js";
import { refusalOf, refusalText, retryRefusedText } from "./jobActions.js";

/** Image MIME types the design upload accepts. */
const IMAGE_ACCEPT = ["image/png", "image/jpeg", "image/webp"];
/** Per image: the cloud refuses a generation image over 20 MB (`ai_max_image_bytes`). */
const MAX_DESIGN_BYTES = 20 * 1024 * 1024;
/** All images of one request: the cloud refuses a body over 64 MB; the rest is the form. */
const MAX_UPLOAD_BYTES = 60 * 1024 * 1024;
/** A mockup-only job, or one whose print guide failed, has no print guide: it never goes on to a template. */
const continuable = (job: GenerationJobView) => isReady(job) && !job.mockupOnly && !guideMissing(job);
/** The views a run's template takes: the newest job of each that can go on, back first. */
const templateJobs = (run: Run) => latestPerView(run).filter(continuable);
/** The views Continue takes: those in no template yet (owner, 2026-10-23: a late or regenerated
 *  view joins the run's template, never a second one; an old run's view may be in another). */
const continueJobs = (run: Run) => templateJobs(run).filter((job) => !job.publishedTemplateId);
/** Our own sentence for a failed pick: it is not a generation, so it carries no code. */
const PICK_FAILED = "That image could not be used. Pick a PNG, JPEG or WebP image up to 20 MB.";
const TOO_LARGE_TOGETHER = "These images are too large together. Keep all the images under 60 MB in total.";


/** Where a customer's template is on its way: the same three steps the wizard shows. */
const GENERATE_STEPS = [
  { key: "generate", label: "Generate", note: "Case, product, views" },
  { key: "mask", label: "Place design", note: "Print area found for you" },
  { key: "publish", label: "Publish", note: "Name and SKU" },
] as const;

/** The shared stepper, with `current` as a 0-based index into `GENERATE_STEPS`. */
function GenerateSteps({ current }: { current: number }) {
  return (
    <ol className="stepper" data-testid="mask-stepper" aria-label="Template progress">
      {GENERATE_STEPS.map((s, i) => {
        const state = i < current ? "done" : i === current ? "active" : "todo";
        return (
          <li key={s.key} className="is-contents">
            <span
              className={`stepper__step stepper__step--${state}`}
              aria-current={state === "active" ? "step" : undefined}
            >
              <span className="stepper__num">
                {state === "done" ? <Icon name="check" size={16} /> : i + 1}
              </span>
              <span className="generate-panel__step-text">
                {s.label}
                <small className="caption">{s.note}</small>
              </span>
            </span>
            {i < GENERATE_STEPS.length - 1 && <span className="stepper__sep" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * One labelled single-image upload slot: a removable thumbnail, or an add dropzone.
 * `slot` names the test ids (`pick-<slot>`, `<slot>-thumb`, `<slot>-remove`).
 *
 * Direction B R4 — this markup used to carry the Wizard's `wizard__image-strip` /
 * `wizard__thumb*` classes, which meant a stylesheet the Wizard owns decided how the
 * customer's Generate screen looked, and `generate.css` then re-declared four of those rules
 * to undo the compact 88x88 tile. The classes are Generate's own now and the rules say what
 * this screen wants directly, with nothing to undo.
 */
/** A picked image with its Remove, or the button that picks one. */
type ImageSlotProps = { slot: "design" | "product" } & (
  | { file: DraftImage; removeLabel: string; onRemove: () => void }
  | { file?: undefined; title: string; onAdd: () => void }
);

function ImageSlot(props: ImageSlotProps) {
  const { slot, file } = props;
  const url = useObjectUrl(file);
  return (
    <div className="generate-panel__upload" data-testid={`${slot}-strip`}>
      {"removeLabel" in props && file && (
        <div className="generate-panel__upload-item" data-testid={`${slot}-thumb`}>
          <RemovableImage src={url} alt={file.name} className="generate-panel__upload-img"
            removeLabel={props.removeLabel} onRemove={props.onRemove}
            removeTestId={`${slot}-remove`} />
          <span className="generate-panel__upload-name muted">{file.name}</span>
        </div>
      )}
      {"onAdd" in props && (
        <button
          type="button"
          className="dropzone dropzone--add"
          onClick={props.onAdd}
          data-testid={`pick-${slot}`}
        >
          <span className="dropzone__icon" aria-hidden="true"><Icon name="arrow-up" size={16}/></span>
          <strong>{props.title}</strong>
          <span className="muted">PNG, JPG or WebP · 20&nbsp;MB</span>
        </button>
      )}
    </div>
  );
}

/**
 * The single-design generation flow (spec 017 US2) — the generate module's only mode
 * now that AI Studio (create-once product templates) has been removed.
 */
interface GeneratePanelProps {
  onTemplateDone?: () => void;
  /** Open a template a generation was published as. */
  onOpenTemplate?: (templateId: string) => void;
  /** False while the page stays mounted but is not the route on screen: polling pauses. */
  active?: boolean;
}

/** An index row older than this is left to the next load rather than polled. */
const INDEX_POLL_MAX_AGE_MS = 6 * 3_600_000;

/** An index row that can still change on its own, and is recent enough to be worth asking. A
 *  clear image in review waits on a person however old it is. */
function worthPolling(job: GenerationJobView, now: number): boolean {
  if (job.status === "clear_review") return true;
  if (!["queued", "processing", "held"].includes(job.status)) return false;
  return !job.createdAt || now - Date.parse(job.createdAt) < INDEX_POLL_MAX_AGE_MS;
}

/** `set` with `key` in it or out of it. */
function toggled(set: ReadonlySet<string>, key: string, on: boolean): ReadonlySet<string> {
  const next = new Set(set);
  if (on) next.add(key);
  else next.delete(key);
  return next;
}

export function SingleDesignPanel({ onTemplateDone, onOpenTemplate, active = true }: GeneratePanelProps) {
  const { client, io, logger } = useServices();
  /** The tab opens on the index of generations; "New generation" opens the setup form. */
  const [showSetup, setShowSetup] = useState(false);
  useEffect(() => { if (!active) setShowSetup(false); }, [active]);
  const index = useGenerationIndex(client);

  const [groups, setGroups] = useState<ResolvedOptionGroup[]>([]);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const { designs, product, details, selected, jobs } = useGenerateDraft();
  /** The first case reference: the one every run is keyed and sent by. */
  const design = designs[0];
  const addDesign = (file: DraftImage) =>
    useGenerateDraft.setState((s) => ({ designs: [...s.designs, file].slice(0, MAX_CASE_REFERENCES) }));
  const removeDesign = (at: number) =>
    useGenerateDraft.setState((s) => ({ designs: s.designs.filter((_, i) => i !== at) }));
  const setProduct = (product?: DraftImage) => useGenerateDraft.setState({ product });
  const setDetails = (next: (d: DraftDetails) => DraftDetails) =>
    useGenerateDraft.setState((s) => ({ details: next(s.details) }));
  const setSelected = (next: (s: Record<string, string[]>) => Record<string, string[]>) =>
    useGenerateDraft.setState((s) => ({ selected: next(s.selected) }));
  const setJobs = (next: DraftJob[] | ((prev: DraftJob[]) => DraftJob[])) =>
    useGenerateDraft.setState((s) => ({ jobs: typeof next === "function" ? next(s.jobs) : next }));

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | { detail: string; reference: string } | null>(null);
  const [pickFailed, setPickFailed] = useState(false);
  const [tooLarge, setTooLarge] = useState(false);

  const wizardOpen = useWizardStore((s) => s.open && s.origin === "generate");
  const publishedRun = useWizardStore((s) => s.open && s.origin === "generate" && s.step === "submit");
  /** The job of the image on the Mask step: its Regenerate and Reject. */
  const wizardJobId = useWizardStore((s) => s.generated?.generationJobId);
  /** The job whose template was just published: its row is marked on the index. */
  const [justPublished, setJustPublished] = useState<string | null>(null);
  /** The job whose result is open in the larger preview modal (null = closed). */
  const [previewId, setPreviewId] = useState<string | null>(null);
  // The entitlement-capped ceiling on how many Views may be generated at once (null = no
  // cap known yet). Sourced from the customer's resolved template-options.
  const [maxViews, setMaxViews] = useState<number | null>(null);
  /** The catalog's active product types (`/api/template-options`): the only ones the cloud accepts. */
  const [productTypes, setProductTypes] = useState<NonNullable<TemplateOptions["product_types"]>>([]);
  /** "Mock-up only" is offered for a product type with a clear pass; any other type makes the print. */
  const [output, setOutput] = useState<GenerationOutput>("print");

  const catalog = usePhoneCatalog();

  /**
   * WS-HF — may this customer generate at all?
   *
   * Three states, not two. `undefined` is "not asked yet", which is what keeps the page from
   * flashing a Generate form it is about to take away.
   *
   * **Only an explicit `false` closes the gate.** Absence is NOT off, and that is the same rule
   * `hosting_enabled` states two fields above it in `CustomizationCapabilities`: a client that
   * read silence as "off" would take a shipped capability away from every customer on every
   * cloud that predates the field — silently, the moment the desktop app auto-updates, and for a
   * reason nobody could see on screen. A failed read is the same case: a transient 503 is not an
   * administrator's decision. The gate is a SERVER FACT, so it takes a server saying so.
   */
  const [mayGenerate, setMayGenerate] = useState<boolean | undefined>(undefined);
  /** Owner rule 2026-09-25: admins read the technical text; a customer reads a code. */
  const admin = useSessionStore((s) => s.isAdmin());

  // On mount, load the admin-defined dropdown options (NO harness text) + the view cap.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const caps = await client.customizationCapabilities?.();
        /* ADMINS BYPASS ON THE CLIENT TOO (wave 3, fix 11). The server exempts them — an
           admin's capabilities answer `true` whatever the switch says — and the client must
           not undo that from a cached or older answer: the gate is a switch this person owns,
           and a notice telling them it is closed would be a form taken away from its owner. */
        const admin = useSessionStore.getState().isAdmin();
        if (!cancelled) setMayGenerate(admin || caps?.customer_generation_enabled !== false);
      } catch {
        // A transient failure is not an administrator's decision — see the note on the state.
        if (!cancelled) setMayGenerate(true);
      }
    })();
    void (async () => {
      try {
        const resolved = await client.getResolvedOptions();
        if (!cancelled) setGroups(resolved.groups);
      } catch (e) {
        if (!cancelled) setOptionsError(e instanceof Error ? e.message : String(e));
      }
    })();
    void (async () => {
      try {
        const opts = await client.templateOptions();
        if (cancelled) return;
        setMaxViews(opts.max_views ?? null);
        const types = opts.product_types ?? [];
        setProductTypes(types);
        // A run always names a product type: the catalog's first until another is picked.
        if (types.length) setDetails((d) => ({ ...d, productType: d.productType || types[0].code }));
      } catch { /* cap is best-effort; leave unlimited if unknown */ }
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  const phoneModel = catalog.models.find((m) => m.id === details.modelId);
  const offersMockupOnly = (code: string) => productTypes.some((t) => t.code === code && t.mockup_only);
  const mockupOnly = offersMockupOnly(details.productType) && output === "mockup";
  const chooseProductType = (productType: string) => {
    setDetails((d) => ({ ...d, productType }));
    if (!offersMockupOnly(productType)) setOutput("print");
  };
  const chooseBrand = (brand: string) => setDetails((d) => ({
    ...d, brand, modelId: phoneModel && phoneModel.brand.toLowerCase() !== brand.toLowerCase() ? "" : d.modelId,
  }));
  const chooseModel = (modelId: string) => setDetails((d) => ({
    ...d, modelId, brand: d.brand || (catalog.models.find((m) => m.id === modelId)?.brand ?? ""),
  }));
  /** An admin's "+ Add new model…" under the chosen brand. */
  const addModel = async (model: string): Promise<boolean> => {
    try {
      chooseModel((await catalog.addModel(details.brand, model)).id);
      return true;
    } catch (e) {
      setError(errorDetail(e));
      return false;
    }
  };

  // The Views axis group (kind === "axis") is the multi-select generation axis. Reserved
  // non-prompt groups (e.g. `format`, an output setting) are NOT shown as prompt dropdowns.
  const axisGroup = useMemo(() => groups.find((g) => g.kind === "axis"), [groups]);
  // Reserved non-prompt modifier keys NOT shown as generate dropdowns: `format` (an output
  // setting) and `background` (Task 6 — background customization moved to the Templates
  // authoring surface, so it no longer rides the per-generation flow here).
  const modifierGroups = useMemo(
    () => groups.filter((g) => g.kind === "modifier" && g.key !== "format" && g.key !== "background"),
    [groups],
  );

  const selectedViewIds = axisGroup ? selected[axisGroup.key] ?? [] : [];

  // Poll this draft's jobs and the recent index rows still running, only while on screen.
  const jobIds = useMemo(() => {
    const now = Date.now();
    return [...new Set([...jobs, ...index.rows.filter((j) => worthPolling(j, now))].map((j) => j.id))];
  }, [jobs, index.rows]);
  const visible = useDocumentVisible();
  /** Back on the page (another tab, another window): the index catches up with other devices. */
  const shown = active && visible;
  const wasShown = useRef(shown);
  useEffect(() => {
    if (shown && !wasShown.current) void index.reloadTop();
    wasShown.current = shown;
  }, [shown, index]);
  const { snapshots, error: pollError, retry: retryPolling } =
    useGenerationJobs(client, jobIds, { paused: !active || !visible, onGone: (id) => dropJob(id) });
  const liveJobs = useMemo(
    () =>
      jobs.map((j) => {
        const job = snapshots[j.id] ?? j.job;
        // Prefer the SERVER-echoed detail fields (survive reload/other devices) over the
        // locally-built string, which only exists for the in-session enqueue call.
        const serverDetails =
          [job.name, job.brand, job.model].filter(Boolean).join(" · ") || undefined;
        return { ...j, job, details: serverDetails ?? j.details };
      }),
    [jobs, snapshots],
  );
  /** The index rows, each at its newest: the poll's snapshot until the stored row is final. */
  const indexEntries: DraftJob[] = useMemo(() => index.rows.map((row) => {
    const job = isTerminal(row) ? row : snapshots[row.id] ?? row;
    const label = axisGroup?.choices.find((c) => c.value === job.view)?.label ?? job.view;
    const details = [job.name, job.brand, job.model].filter(Boolean).join(" · ")
      || jobs.find((d) => d.id === row.id)?.details;
    return { id: row.id, label, job, details };
  }), [index.rows, snapshots, axisGroup, jobs]);
  const indexRuns = useMemo(() => groupRuns(indexEntries.map((e) => e.job)), [indexEntries]);
  /** The newest run with a view in no template yet, and that view. */
  const nextRun = indexRuns.find((run) => continueJobs(run).length > 0);
  const nextToContinue = nextRun && continueJobs(nextRun)[0];
  const liveRuns = useMemo(() => groupRuns(liveJobs.map((e) => e.job)), [liveJobs]);
  /** Every live run's views by state, for the results header. */
  const liveCounts = useMemo(() => liveRuns.map(runCounts).reduce((sum, c) => ({
    review: sum.review + c.review, generating: sum.generating + c.generating,
    ready: sum.ready + c.ready, failed: sum.failed + c.failed,
  }), { review: 0, generating: 0, ready: 0, failed: 0 }), [liveRuns]);
  const nextLiveRun = liveRuns.find((run) => continueJobs(run).length > 0);
  /** The run whose gallery replaces the list (null = the list), on the screen shown; a person's
   *  open moves focus into it, an open by itself does not. Each screen, and a return to the tab,
   *  starts on its list. */
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  useEffect(() => { setOpenRunId(null); }, [showSetup, active]);
  const [openedByHand, setOpenedByHand] = useState(false);
  const openRun = (run: Run) => {
    setOpenRunId(run.runId);
    setOpenedByHand(true);
  };
  /** The run whose gallery was just left: its entry takes focus back, once. */
  const [backFrom, setBackFrom] = useState<string | null>(null);
  useEffect(() => { if (backFrom) setBackFrom(null); }, [backFrom]);
  const backToList = () => {
    setBackFrom(openRunId);
    setOpenRunId(null);
  };
  /** A run started on this page opens by itself once its first image is ready, and only once,
   *  on the setup screen; the runs of an earlier session never take the screen. */
  const autoOpened = useRef(new Set<string>());
  useEffect(() => {
    const ready = liveRuns.filter((run) => !autoOpened.current.has(run.runId) && run.jobs.some(hasImage));
    if (ready.length === 0) return;
    for (const run of ready) autoOpened.current.add(run.runId);
    if (openRunId || !showSetup) return;
    setOpenRunId(ready[0].runId);
    setOpenedByHand(false);
  }, [liveRuns, openRunId, showSetup]);

  /** Toggle a value in a multi-select group's selection set. */
  const toggleMulti = (key: string, id: string) => {
    setSelected((prev) => {
      const cur = prev[key] ?? [];
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      return { ...prev, [key]: next };
    });
  };

  /** Set a single-select group's selection (one value, or none). */
  const setSingle = (key: string, id: string) => {
    setSelected((prev) => ({ ...prev, [key]: id ? [id] : [] }));
  };

  /** Pick one image into a slot; `replacing` is the image the slot already holds. */
  const pickImage = async (set: (f: DraftImage) => void, replacing?: DraftImage) => {
    setError(null);
    setPickFailed(false);
    setTooLarge(false);
    try {
      const [f] = await io.pickFiles({ accept: IMAGE_ACCEPT, multiple: false, maxBytes: MAX_DESIGN_BYTES });
      if (!f) return;
      const held = [...designs, product].reduce((sum, img) => sum + (img?.bytes.length ?? 0), 0);
      if (held - (replacing?.bytes.length ?? 0) + f.bytes.length > MAX_UPLOAD_BYTES) {
        setTooLarge(true);
        return;
      }
      set({ name: f.name, bytes: f.bytes, mime: f.mime });
    } catch (e) {
      logger?.log("warn", "GeneratePanel · image pick failed", { error: e });
      setPickFailed(true);
    }
  };

  // All selected modifier choice ids (applied to every view's job).
  const selectedModifierIds = useMemo(() => {
    const ids: string[] = [];
    for (const g of modifierGroups) ids.push(...(selected[g.key] ?? []));
    return ids;
  }, [modifierGroups, selected]);

  // WS-HF N6 — set when a device run refused because an earlier submission's outcome is
  // unknown. Pressing Generate again cannot clear it, by design; only the operator can, after
  // looking at their Higgsfield account.
  const [stuck, setStuck] = useState<{ jobId: string; view: string } | null>(null);
  /** M3 — a paid image whose collection failed twice; the operator may give it up. */
  const [abandonable, setAbandonable] = useState<{ jobId: string; view: string } | null>(null);
  /** M2 — which chosen views a re-press did not run, and why. */
  const [note, setNote] = useState<string | { detail: string; reference: string } | null>(null);
  // WS-HF N7 — this device cannot remember a submission across a reload, so the protection
  // above is weaker than it looks. Probed once, at mount.
  const [canRemember] = useState(storageAvailable);
  /** Whether the operator has acknowledged N7's warning for the press they are about to make. */
  const [retryConfirmed, setRetryConfirmed] = useState(false);
  /** True once a device run in this session has failed: the NEXT press is a retry. */
  const [retriedAfterFailure, setRetriedAfterFailure] = useState(false);
  const needsRetryConfirm = !canRemember && retriedAfterFailure && !retryConfirmed;

  // One print area for now (owner, 2026-10-06): the choice is off, and the cloud prints the
  // whole back whatever is sent.
  const printArea: GenerationPrintArea = "whole";
  // The grounding rule: a run names the real phone, so brand AND model are required.
  const productNamed = Boolean(details.brand.trim() && phoneModel);
  // A held job may already be billed; a customer asks an admin instead of paying again.
  const heldForCustomer = admin ? undefined : liveJobs.find((e) => e.job.status === "held");
  const canGenerate = Boolean(design) && productNamed && selectedViewIds.length > 0 && !busy && !stuck
    && !needsRetryConfirm && !heldForCustomer;

  /**
   * ENQUEUE one server-side job per selected view (N views → N enqueue calls, each
   * for ONE view). Returns immediately — generation runs in the background and the
   * poll hook drives per-view status. Does NOT await generation.
   */
  /** Enqueue `viewIds`; `replacing` is the row a Regenerate replaces (the others stay). */
  const generate = async (viewIds: string[] = selectedViewIds, replacing?: DraftJob) => {
    if (!design || !axisGroup || viewIds.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    setNote(null);
    setAbandonable(null);
    setPickFailed(false);
    setStuck(null);
    setJobs(replacing ? (prev) => prev.filter((j) => j.id !== replacing.id) : []);
    if (!replacing) setOpenRunId(null);
    setPreviewId(null); // close any open preview when starting a fresh batch
    // Map blank inputs to undefined so the platform client never sends empty-string
    // multipart fields (jsonClient.ts's enqueueGeneration only appends a field when
    // it is truthy).
    // A Regenerate makes its view again with the Details that view was made with.
    const made = replacing?.job;
    const detailsPayload = made ? {
      productType: made.productType || undefined,
      brand: made.brand || undefined,
      model: made.model || undefined,
      name: made.name?.trim() || undefined,
      phoneModelId: made.phoneModelId || undefined,
      caseStyleId: made.caseStyleId || undefined,
      baseType: made.baseType || undefined,
    } : {
      productType: details.productType.trim() || undefined,
      brand: details.brand.trim() || undefined,
      model: phoneModel?.model,
      // Wire key stays `name` (unchanged platform/cloud contract); the UI/state field
      // is `skuPrefix`.
      name: details.skuPrefix.trim() || undefined,
      phoneModelId: phoneModel?.id,
      // Stored on the job for the template; the prompt never reads it.
      caseStyleId: details.caseStyleId || undefined,
      baseType: details.baseType || undefined,
    };
    const moreCaseImages = designs.slice(1).map(({ bytes, mime }) => ({ bytes, mime }));
    // One run per press: the cloud groups the views enqueued below by it. A Regenerate keeps
    // its row's Output and run, whatever the page shows now.
    const runId = replacing?.job.runId ?? crypto.randomUUID().replace(/-/g, "");
    const output = (replacing ? replacing.job.mockupOnly : mockupOnly) ? "mockup" : undefined;
    // Sub-line shown under each job row once enqueued — "name · brand · model" (the
    // parts that were actually filled in), never the product type.
    const jobDetailsSub =
      [detailsPayload.name, detailsPayload.brand, detailsPayload.model].filter(Boolean).join(" · ") || undefined;
    const chosen = viewIds
      .map((viewId) => ({ viewId, choice: axisGroup.choices.find((c) => c.id === viewId) }))
      .filter((x): x is { viewId: string; choice: NonNullable<typeof x.choice> } => !!x.choice);
    try {
      // WS-HF: on a desktop connected to Higgsfield, the operator's OWN subscription runs the
      // generation. The cloud still creates and owns every job — `runDeviceGeneration` reports
      // each one the moment it exists (`onJobCreated`), so the poll below drives the same rows,
      // the same badges and the same "Continue to masking" as a cloud run.
      const mcp = higgsfieldMcp();
      const labelOf = new Map(chosen.map((c) => [c.choice.value, c.choice.label]));
      const chosenViews = chosen.map((c) => c.choice.value);
      // A run this computer started may be paid for; only the device path may resume it, and
      // only those views run: a view that already finished would be charged again (M2).
      const unsettled = mayGenerateOnThisComputer()
        ? await unsettledDeviceRuns(client, design.bytes, chosenViews)
        : [];
      const views = unsettled.length ? unsettled.map((u) => u.view) : chosenViews;
      // Only a super-admin's own sign-in is ever used, and only while the company
      // subscription is not connected; a designer's run is always the cloud's.
      const onDevice = unsettled.length
        ? mcp.hfMcpAvailable
        : await thisComputerMayGenerate(mcp, client, logger);
      const hfStatus = onDevice ? await mcp.hfMcpStatus() : { connected: false };
      const source = resolveGenerationSource(onDevice, hfStatus.connected);
      if (source !== "higgsfield-mcp" && unsettled.length) throw unsettledRunError(unsettled[0]);
      if (source === "higgsfield-mcp") {
        const skipped = chosenViews.filter((v) => !views.includes(v));
        if (skipped.length) setNote(onlyUnsettledNote(views.map(labelOf.get, labelOf), skipped.map(labelOf.get, labelOf)));
        const choiceIdOf = new Map(chosen.map((c) => [c.choice.value, c.viewId]));
        await runDeviceGeneration({
          client,
          inputPng: design.bytes,
          inputMime: design.mime,
          productPng: product?.bytes,
          productMime: product?.mime,
          moreCaseImages,
          views,
          // Per view, like the cloud path: the VIEW's own choice id first, then the shared
          // modifier ids.
          optionChoiceIds: (view) => [choiceIdOf.get(view), ...selectedModifierIds]
            .filter((x): x is string => !!x),
          details: detailsPayload,
          printArea,
          output,
          runId,
          mcp,
          downloadBytes: io.downloadBytes?.bind(io),
          onJobCreated: (view, job) => {
            setJobs((prev) => [
              ...prev,
              { id: job.id, label: labelOf.get(view) ?? view, job, details: jobDetailsSub },
            ]);
            index.putOnTop([job]);
          },
        });
        return;
      }

      const entries: DraftJob[] = [];
      for (const { viewId, choice } of chosen) {
        const enqueued = await client.enqueueGeneration(design.bytes, [choice.value], {
          optionChoiceIds: [viewId, ...selectedModifierIds],
          mime: design.mime,
          details: detailsPayload,
          moreCaseImages,
          productImage: product ? { bytes: product.bytes, mime: product.mime } : undefined,
          clearReview: true,
          printArea,
          output,
          runId,
        });
        for (const job of enqueued) entries.push({ id: job.id, label: choice.label, job, details: jobDetailsSub });
      }
      setJobs((prev) => [...prev, ...entries]);
      index.putOnTop(entries.map((e) => e.job));
    } catch (e) {
      setError(errorDetail(e));
      // N6: the one failure a retry cannot fix on its own.
      // R3 — the error names its OWN view. `chosen[0]` was the first view of the run, not the
      // one that got stuck, so the way out used to clear the wrong entry.
      if (isStuckOnUnknownSubmission(e) && e.jobId && e.view) {
        setStuck({ jobId: e.jobId, view: e.view });
      }
      if (e instanceof PaidCollectError && e.jobId && e.view) setAbandonable({ jobId: e.jobId, view: e.view });
      // N7: on a device that cannot remember submissions, the NEXT press is an unprotected
      // retry and has to be acknowledged.
      setRetriedAfterFailure(true);
      setRetryConfirmed(false);
    } finally {
      setBusy(false);
    }
  };

  /** N6 — the operator has checked their Higgsfield account; allow a fresh run. */
  const clearStuck = async () => {
    if (!stuck || !design) return;
    await clearStuckDeviceRun(stuck.jobId, design.bytes, stuck.view);
    setStuck(null);
    setError(null);
  };

  /** M3 — give up a paid image that cannot be collected; its cloud job is failed. */
  const abandonPaid = async () => {
    if (!abandonable || !design) return;
    try {
      await abandonPaidDeviceRun(client, abandonable.jobId, design.bytes, abandonable.view);
      setAbandonable(null);
      setError(null);
    } catch (e) {
      setError(errorDetail(e));
    }
  };

  /** A3 — the view a job was made for, as its option choice id. */
  const viewIdOf = (entry: DraftJob) => axisGroup?.choices.find((c) => c.value === entry.job.view)?.id;
  const regenerate = (entry: DraftJob) => {
    const viewId = viewIdOf(entry);
    if (viewId) void generate([viewId], entry);
  };
  const reject = (entry: DraftJob) => setJobs((prev) => prev.filter((j) => j.id !== entry.id));
  const regenerateQuestion = (entry: DraftJob) => {
    const { credits, estCostUsd } = entry.job;
    const cost = credits ? `about ${credits} credits` : estCostUsd > 0 ? `about $${estCostUsd.toFixed(2)}` : null;
    return `Regenerate ${entry.label}? It is one more paid generation${cost ? `, ${cost}` : ""}.`;
  };
  /** Regenerate / Reject on a row: only while its job's inputs are still in this draft. */
  const retakeOf = (entry: DraftJob) => jobs.some((j) => j.id === entry.id)
    ? { question: regenerateQuestion(entry), regenerate: () => regenerate(entry), reject: () => reject(entry) }
    : undefined;
  /** The Mask step's Regenerate / Reject: both leave the wizard for this page, so a run's
   *  template loses the work on its other views. */
  const retakeFor = (jobId: string | undefined, run: boolean) => {
    const entry = liveJobs.find((j) => j.id === jobId);
    if (!entry) return undefined;
    const leave = () => useWizardStore.getState().reset();
    return {
      question: run ? `${regenerateQuestion(entry)} This closes the template, and the work on its other views is cleared.`
        : regenerateQuestion(entry),
      regenerate: () => { leave(); regenerate(entry); },
      reject: () => { leave(); reject(entry); },
    };
  };

  /** The wizard's Details from what the job was made with, so an earlier session's job works too. */
  const wizardDetailsFor = (job: GenerationJobView) => {
    const same = (a?: string | null, b?: string | null) => (a ?? "").toLowerCase() === (b ?? "").toLowerCase();
    // An older job has no catalog link: its brand and model text find the model instead.
    const model = job.phoneModelId
      ? catalog.models.find((m) => m.id === job.phoneModelId)
      : catalog.models.find((m) => same(m.brand, job.brand) && same(m.model, job.model));
    return {
      category: job.productType || PRODUCT_CATEGORIES[0].value,
      brand: model?.brand ?? job.brand ?? "",
      phoneModelId: model?.id ?? job.phoneModelId ?? "",
      caseStyleId: job.caseStyleId ?? "",
      baseType: job.baseType ?? "",
      code: job.name?.trim() ?? "",
      templateName: model ? modelLabel(model) : "",
    };
  };

  /** The result and its optional clear layer; a failed clear layer must not block the template. */
  const downloadResult = (job: GenerationJobView) => Promise.all([
    fetchResultBytes(job.resultUrl ?? ""),
    job.clearUrl ? fetchResultBytes(job.clearUrl).catch(() => undefined) : undefined,
  ]);

  /** The BYTES, never the signed URL: the editor must not break on an expired link. A row's
   *  links were signed when it was listed, so a failed download asks for fresh ones once. */
  const wizardImage = async (job: GenerationJobView): Promise<GeneratedImage> => {
    const [{ bytes, mediaType }, clear] = await downloadResult(job)
      .catch(async () => downloadResult(await index.refresh(job.id)));
    return {
      dataUri: toDataUri(bytes, mediaType), bytes, mediaType, generationJobId: job.id,
      checkNotes: job.checkNotes, printAreaMode: job.printArea, clear,
    };
  };

  /** Continue downloads every view's images first; the button says so meanwhile. */
  const [opening, setOpening] = useState(false);
  const whileOpening = async (open: () => Promise<void>) => {
    setOpening(true);
    try {
      await open();
    } finally {
      setOpening(false);
    }
  };

  /** A2 — the chosen completed result becomes a template: the wizard's Mask step, prefilled. */
  const continueToTemplate = (entry?: DraftJob) => whileOpening(async () => {
    if (!entry || !continuable(entry.job) || !entry.job.resultUrl) return;
    setError(null);
    try {
      const image = await wizardImage(entry.job);
      setPreviewId(null);
      setJustPublished(null);
      openWizardOnGeneration(image, { ...wizardDetailsFor(entry.job), views: [wizardView(entry.job.view)] });
    } catch (e) {
      setError({ detail: refusalText(e, { admin, reference: entry.id }), reference: entry.id });
    }
  });

  /** One template with every view of a run (`jobs`: its template views, back first). */
  const continueRun = (jobs: GenerationJobView[]) => whileOpening(async () => {
    setError(null);
    let failed = jobs[0].id;
    try {
      const views = await Promise.all(jobs.map(async (job): Promise<RunView> => {
        const image = await wizardImage(job).catch((e) => { failed = job.id; throw e; });
        return { view: job.view, image, clear: image.clear?.bytes, generationJobId: job.id };
      }));
      setPreviewId(null);
      setJustPublished(null);
      openWizardOnRun(views, wizardDetailsFor(jobs[0]));
    } catch (e) {
      setError({ detail: refusalText(e, { admin, reference: failed }), reference: failed });
    }
  });

  /** A run's template open in the editor, taking the run's views not yet in it. */
  const [intoTemplate, setIntoTemplate] = useState<{ template: ProductTemplate; views: RunViewIntoTemplate[] } | null>(null);
  const continueIntoTemplate = (templateId: string, jobs: GenerationJobView[]) => whileOpening(async () => {
    setError(null);
    let failed = jobs[0].id;
    try {
      const views = await Promise.all(jobs.map(async (job): Promise<RunViewIntoTemplate> => {
        const image = await wizardImage(job).catch((e) => { failed = job.id; throw e; });
        return { view: job.view, jobId: job.id, image, clear: image.clear?.bytes };
      }));
      const template = await client.getTemplate(templateId);
      setPreviewId(null);
      setJustPublished(null);
      setIntoTemplate({ template, views });
    } catch (e) {
      setError({ detail: refusalText(e, { admin, reference: failed }), reference: failed });
    }
  });
  /** The page's Continue: named "Continue" whatever its hint says, and busy while it opens. */
  const continueButton = (onClick: () => void, enabled: boolean, title: string) => (
    <button type="button" className="btn" onClick={onClick} disabled={!enabled || opening}
      aria-label={opening ? "Opening" : "Continue"} aria-busy={opening || undefined} title={title}
      data-testid="continue-to-masking">
      {opening ? "Opening…" : "Continue"}
    </button>
  );
  /** The preview's Continue: a view of a run that has a template joins that template, and a view
   *  already in a template opens it. */
  const continueEntry = (entry: DraftJob) => {
    const run = [...indexRuns, ...liveRuns].find((r) => r.runId === runKey(entry.job));
    const templateId = run && runTemplateId(run);
    if (!templateId) return void continueToTemplate(entry);
    if (!entry.job.publishedTemplateId) return void continueIntoTemplate(templateId, [entry.job]);
    setPreviewId(null);
    onOpenTemplate?.(entry.job.publishedTemplateId);
  };
  const closeTemplate = () => {
    setIntoTemplate(null);
    notifyCatalogChanged("template");
  };

  /** The job whose Cancel or Remove request is out: its row's actions wait. */
  const [acting, setActing] = useState<string | null>(null);
  const dropJob = (id: string) => {
    index.remove(id);
    setJobs((prev) => prev.filter((j) => j.id !== id));
  };
  const putJob = (job: GenerationJobView) => {
    index.replace(job);
    setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, job } : j)));
  };
  const showJob = (job: GenerationJobView) => {
    putJob(job);
    // The poll's snapshot of this job is the old one: ask for it now.
    retryPolling();
  };
  /** A signed link expired: the row is asked for again. A row the poll still asks about, and this
   *  page's own job, show the poll's copy, which is as old as the link. */
  const expiredLink = useExpiredLinkRefresh(async (id) => {
    const job = await index.refresh(id);
    if (!isTerminal(job) || jobs.some((j) => j.id === id)) showJob(job);
  });
  /** A job a gallery got back: a decision moved it on, or a Regenerate added it to its run. The
   *  gallery asks the poll once its requests end. */
  const takeJob = (job: GenerationJobView) => {
    if (!index.rows.some((r) => r.id === job.id)) index.putOnTop([job]);
    if (!jobs.some((j) => j.id === job.id) && jobs.some((j) => runKey(j.job) === runKey(job))) {
      const label = axisGroup?.choices.find((c) => c.value === job.view)?.label ?? viewLabel(job.view);
      setJobs((prev) => [...prev, { id: job.id, label, job }]);
    }
    putJob(job);
  };
  /** The one way a run continues: a run with a template adds its views not in it to that
   *  template; otherwise one template with every ready view, and a single view goes on alone. */
  const onContinue = (run: Run) => {
    const jobs = continueJobs(run);
    const templateId = runTemplateId(run);
    if (templateId && jobs.length > 0) void continueIntoTemplate(templateId, jobs);
    else if (jobs.length > 1) void continueRun(jobs);
    else if (jobs[0]) void continueToTemplate({ id: jobs[0].id, label: viewLabel(jobs[0].view), job: jobs[0] });
  };
  /** Cancel (the cloud answers with the job) or Remove (it answers with nothing) a row. The
   *  caller clears the note first, so a refusal in a batch stays on screen. */
  const act = async (id: string, request: () => Promise<GenerationJobView | void>, refused?: string) => {
    setActing(id);
    try {
      const job = await request();
      if (job) showJob(job);
      else dropJob(id);
    } catch (e) {
      const refusal = refusalOf(e);
      if (refusal === "gone") return dropJob(id);
      setNote({ detail: refused && refusal === "finished" ? refused : refusalText(e, { admin, reference: id }), reference: id });
      if (refusal === "finished") void index.refresh(id).then(showJob).catch(() => undefined);
    } finally {
      setActing(null);
    }
  };
  /** Remove runs: every job of each, older images of a view too. A refused job stays. */
  const removeRuns = async (runs: Run[], onRunDone?: (done: number) => void) => {
    setNote(null);
    let done = 0;
    for (const run of runs) {
      for (const job of run.jobs) await act(job.id, () => client.removeGenerationJob(job.id));
      onRunDone?.(++done);
    }
  };
  // Bulk Remove (owner, 2026-10-15: "bulk remove all old gen and templates"): the run's Remove,
  // once per checked run on screen, after ONE confirmation naming the count.
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [removing, setRemoving] = useState<{ done: number; total: number } | null>(null);
  const pick = (runId: string, on: boolean) => setPicked((prev) => toggled(prev, runId, on));
  const removePicked = async (runs: Run[]) => {
    if (removing || runs.length === 0) return;
    const many = runs.length === 1 ? "this generation" : `these ${runs.length} generations`;
    if (!(await confirmAction(`Remove ${many}? Running ones stop. The templates made from them stay.`))) return;
    setRemoving({ done: 0, total: runs.length });
    try {
      await removeRuns(runs, (done) => setRemoving({ done, total: runs.length }));
      setPicked(new Set());
    } finally {
      setRemoving(null);
    }
  };
  const rowActions = (id: string) => ({
    onCancel: () => { setNote(null); void act(id, () => client.cancelGenerationJob(id)); },
    onRetryMagenta: () => { setNote(null); void act(id, () => client.retryMagentaGenerationJob(id), retryRefusedText(id)); },
    busy: acting === id,
  });
  const runBusy = (run: Run) => run.jobs.some((j) => j.id === acting);

  const finishPublish = (jobIds: string[]) => {
    setJustPublished(jobIds[0] ?? null);
    if (dropPublishedJobs(jobIds)) onTemplateDone?.();
  };
  /** A finished publish is not left open behind the tab: coming back shows the index. */
  useEffect(() => {
    if (active || !publishedRun) return;
    const jobIds = generationJobIds(useWizardStore.getState());
    useWizardStore.getState().reset();
    setJustPublished(jobIds[0] ?? null);
    dropPublishedJobs(jobIds);
  }, [active, publishedRun]);
  const pollBanner = pollError && (
    <div className="banner banner--danger" data-testid="generate-poll-error">
      <span>{pollError}</span>
      <button type="button" className="btn btn--sm" onClick={retryPolling}
              data-testid="generate-poll-retry">Retry</button>
    </div>
  );

  /* The larger result preview is the SHARED modal (Direction B R1): Escape, focus move and
     focus return come from `ui/Modal.tsx` and `ui/dialog.ts`. */
  const previewEntry = (showSetup ? liveJobs : indexEntries).find((e) => e.id === previewId);
  const previewSrc = useGuideSafeUrl(previewEntry?.job.resultUrl);
  const previewModal = previewEntry && isReady(previewEntry.job) && (
    <Modal
      testid="result-preview-modal"
      title={previewEntry.label}
      badge={<span className={`badge badge--${jobBadge(previewEntry.job).variant}`}>{jobLabel(previewEntry.job)}</span>}
      onClose={() => setPreviewId(null)}
      footer={continuable(previewEntry.job) ? (
        <button type="button" className="btn btn--primary" onClick={() => continueEntry(previewEntry)}
          data-testid="result-preview-continue">
          Continue
        </button>
      ) : undefined}
    >
      {previewSrc && (
        <img className="generate-panel__preview-img" src={previewSrc}
          alt={`Generated view: ${previewEntry.label}`} data-testid="result-preview-img"
          onError={() => expiredLink.onError(previewEntry.id)} onLoad={() => expiredLink.onLoad(previewEntry.id)} />
      )}
    </Modal>
  );

  /** A run's gallery in the results column; under its preview, the chosen view's own row. A cloud
   *  view's Regenerate is the gallery's; a view made on this computer keeps its row's. */
  const gallery = (run: Run, entries: DraftJob[], showSteps: boolean) => (
    <RunGallery key={run.runId} run={run} admin={admin} onBack={backToList} autoFocus={openedByHand}
      onJob={takeJob} onSettled={retryPolling} onImageError={expiredLink.onError} onImageLoad={expiredLink.onLoad}
      viewRow={(job) => {
        const entry = entries.find((e) => e.id === job.id) ?? { id: job.id, label: viewLabel(job.view), job };
        const published = job.publishedTemplateId;
        return (
          <ul className="generate-panel__job-rows">
            <JobRow entry={entry} i={0} admin={admin} active={false} thumb={false}
              onPreview={() => setPreviewId(entry.id)}
              onOpenTemplate={published && onOpenTemplate ? () => onOpenTemplate(published) : undefined}
              retake={job.executor === "device" ? retakeOf(entry) : undefined} showSteps={showSteps} {...rowActions(entry.id)} />
          </ul>
        );
      }} />
  );
  const openIndexRun = indexRuns.find((run) => run.runId === openRunId);
  const openLiveRun = liveRuns.find((run) => run.runId === openRunId);
  /** While a gallery is open the action bar's Continue continues that run; a Mock-up only run has
   *  none, and a run whose every ready view is in a template opens that template instead. */
  const continueOpenRun = (run: Run) => {
    if (mockupOnlyRun(run)) return null;
    const ready = templateJobs(run);
    const open = continueJobs(run).length > 0;
    const published = runTemplateId(run);
    if (!open && published && onOpenTemplate) {
      return (
        <button type="button" className="btn" onClick={() => onOpenTemplate(published)} data-testid="generate-open-template">
          Open template
        </button>
      );
    }
    return continueButton(() => onContinue(run), open,
      open ? (published ? "Add every ready view of this run not yet in its template to that template"
        : "Place your design on every ready view of this run not yet in a template, in one template")
        : ready.length > 0 ? "Every ready view of this run is in a template" : "No view of this run is ready yet");
  };

  if (intoTemplate) {
    return (
      <TemplateEditor template={intoTemplate.template} runViews={intoTemplate.views}
        onGenerationPublished={index.replace} onClose={closeTemplate} />
    );
  }

  if (wizardOpen) {
    const jobIds = generationJobIds(useWizardStore.getState());
    return (
      <Wizard onDone={() => finishPublish(jobIds)} onClose={() => useWizardStore.getState().reset()}
        retake={retakeFor(wizardJobId, jobIds.length > 1)} onGenerationPublished={index.replace} />
    );
  }

  /* WS-HF — the customer-generation gate.

     NEITHER screen is drawn until the answer is in. Rendering the form first and taking it
     away is a Generate button the customer may already have pressed. */
  if (mayGenerate === undefined) {
    return (
      <section className="generate-panel" data-testid="generate-panel">
        <p className="faint" data-testid="generate-gate-loading">Loading…</p>
      </section>
    );
  }

  /* Off is the DEFAULT, and the notice is a plain sentence and a link, not a locked form: a
     greyed-out Generate reads as "this is broken", and a customer who cannot tell the two apart
     writes to support. Their route is Mock-ups, so the notice points straight at it. */
  if (mayGenerate === false) {
    return (
      <section className="generate-panel" data-testid="generate-panel">
        <header className="generate-panel__head">
          <h1 className="generate-panel__title panel-title">Generate your views</h1>
        </header>
        <EmptyState
          icon="generate"
          title="Generation is off for customers — place your artwork in Mock-ups"
          hint="Your workspace generates the case images. Open Mock-ups to place your artwork on them and export."
          testId="generate-disabled"
          action={
            /* `.btn--sm` carries the shared 32px hit ring (G-26); a bare anchor would have
               neither that nor a target a finger can find. */
            <a className="btn btn--sm" href="#export" data-testid="generate-disabled-mockups">
              Go to Mock-ups
            </a>
          }
        />
      </section>
    );
  }

  if (!showSetup) {
    return (
      <section className="generate-panel" data-testid="generate-panel">
        <header className="generate-panel__head">
          <h1 className="generate-panel__title panel-title">
            Generations <InfoHint text="Everything you generated, newest first. A generation keeps running when you leave this page." />
          </h1>
        </header>
        {error && (
          <p className="banner banner--danger" role="alert" data-testid="generate-error">{errorNotice(error)}</p>
        )}
        {note && <p className="banner banner--warn" role="status" data-testid="generate-note">{typeof note === "string" ? note : <ErrorNotice detail={note.detail} reference={note.reference} />}</p>}
        <section className="inspector generate-panel__results" aria-label="Generations" data-testid="generate-index">
          <header className="inspector__head">Generations</header>
          <div className="inspector__body">
            {index.error && (
              <div className="banner banner--danger" data-testid="generate-index-error">
                <ErrorNotice detail={index.error} />
                <button type="button" className="btn btn--sm" onClick={index.retry} data-testid="generate-index-retry">
                  Retry
                </button>
              </div>
            )}
            {pollBanner}
            {openIndexRun ? gallery(openIndexRun, indexEntries, true) : (
              <>
                {indexRuns.length > 0 && (() => {
                  const pickedShown = indexRuns.filter((run) => picked.has(run.runId));
                  const all = pickedShown.length === indexRuns.length;
                  return (
                    <div className="generate-panel__bulk" data-testid="generate-bulk">
                      <label className="generate-panel__bulk-all">
                        <input type="checkbox" checked={all}
                          ref={(el) => { if (el) el.indeterminate = pickedShown.length > 0 && !all; }}
                          onChange={() => setPicked(all ? new Set() : new Set(indexRuns.map((run) => run.runId)))}
                          data-testid="generate-select-all" />
                        Select all {indexRuns.length} shown
                      </label>
                      {pickedShown.length > 0 && (
                        <button type="button" className="btn btn--sm btn--danger" onClick={() => void removePicked(pickedShown)}
                          disabled={!!removing} data-testid="generate-bulk-remove">
                          {removing ? `Removing ${removing.done}/${removing.total}…` : `Remove (${pickedShown.length})`}
                        </button>
                      )}
                    </div>
                  );
                })()}
                {indexEntries.length === 0 ? (
                  <EmptyState icon="image" size="sm" title="No generations yet"
                    hint="Start a new generation. It keeps running when you leave this page." testId="generate-empty" />
                ) : (
                  <ul className="generate-panel__job-rows" data-testid="job-status-list">
                    {indexRuns.map((run, r) => (
                      <RunRow key={run.runId} run={run} i={r} onOpen={openRun} autoFocus={run.runId === backFrom}
                        onRemove={(run) => void removeRuns([run])} busy={runBusy(run)}
                        active={run.jobs.some((j) => j.id === (justPublished ?? nextToContinue?.id))}
                        selected={picked.has(run.runId)} onSelect={(on) => pick(run.runId, on)}
                        onImageError={expiredLink.onError} onImageLoad={expiredLink.onLoad} />
                    ))}
                  </ul>
                )}
                {index.hasMore && (
                  <button type="button" className="btn btn--sm" onClick={index.loadMore} data-testid="generate-index-more">
                    Load more
                  </button>
                )}
              </>
            )}
          </div>
        </section>
        <ActionBar label="Generation index actions" primary={
          <button type="button" className="btn btn--primary" onClick={() => setShowSetup(true)} data-testid="generate-new">
            New generation
          </button>
        }>
          {openIndexRun ? continueOpenRun(openIndexRun) : (
            continueButton(() => nextRun && onContinue(nextRun), Boolean(nextRun),
              "Place your design on the newest generation with views not yet in a template, in one template")
          )}
        </ActionBar>
        {previewModal}
      </section>
    );
  }

  return (
    <section className="generate-panel" data-testid="generate-panel">
      <header className="generate-panel__head">
        <h1 className="generate-panel__title panel-title">Generate your views <InfoHint text="Upload the case and the product, name the phone, pick your views, and generate." /></h1>
      </header>

      {optionsError && (
        <p className="banner banner--danger" role="alert" data-testid="options-error">
          <ErrorNotice detail={optionsError} />
        </p>
      )}
      {heldForCustomer && (
        <p className="banner banner--warn" role="status" data-testid="generate-held">
          <ErrorNotice detail={heldForCustomer.job.error ?? "held"} reference={heldForCustomer.job.id} />
        </p>
      )}
      {pickFailed && (
        <p className="banner banner--danger" role="alert" data-testid="generate-pick-error"><ErrorNotice detail={PICK_FAILED} /></p>
      )}
      {tooLarge && (
        <p className="banner banner--danger" role="alert" data-testid="generate-size-error">{TOO_LARGE_TOGETHER}</p>
      )}
      {error && (
        <p className="banner banner--danger" role="alert" data-testid="generate-error">{errorNotice(error)}</p>
      )}

      {note && <p className="banner banner--warn" role="status" data-testid="generate-note">{typeof note === "string" ? note : <ErrorNotice detail={note.detail} reference={note.reference} />}</p>}

      {/* WS-HF N6 — the ONLY way out of an unknown submission. Pressing Generate again cannot
          clear it (the latch refuses on purpose), because only the operator can find out
          whether their Higgsfield account was billed. The label says what they are
          confirming. */}
      {stuck && (
        <p className="banner banner--danger" data-testid="hf-stuck">
          <button type="button" className="btn btn--sm" data-testid="hf-clear-stuck"
            onClick={() => void clearStuck()}>
            I checked my Higgsfield account — clear and allow a new run
          </button>
        </p>
      )}

      {abandonable && (
        <p className="banner banner--danger" data-testid="hf-abandon">
          <button type="button" className="btn btn--sm" data-testid="hf-abandon-paid"
            onClick={() => void abandonPaid()}>
            Give up this paid image and allow a new, charged run
          </button>
        </p>
      )}

      {/* WS-HF N7 — this device cannot remember a submission across a reload, so a retry
          after a failure is NOT protected from billing twice. Said plainly, and the press has
          to be acknowledged rather than silently weakened. */}
      {needsRetryConfirm && (
        <p className="banner banner--danger" data-testid="hf-retry-unprotected">
          This computer cannot remember submissions, so a retry may be billed twice.
          <button type="button" className="btn btn--sm" data-testid="hf-confirm-retry"
            onClick={() => setRetryConfirmed(true)}>
            Generate anyway
          </button>
        </p>
      )}

      {/* Direction B: the form on the left, the live results on the right, and the page's one
          commit in the bar at the bottom. The card that used to wrap both is an `.inspector`
          pair — a dense column of controls wants a 12/500 header and a hairline, not a
          24px-padded card. */}
      <div className="generate-panel__grid">
        <section className="inspector generate-panel__form" aria-label="Setup">
          <header className="inspector__head">Setup</header>
          <div className="inspector__body">
            <div className="field generate-panel__design">
              <span className="label">
                Case reference{" "}
                <InfoHint text="Any photo with the case in it — a lifestyle shot, a hand, a printed design. Only the case is used. Up to five." />
              </span>
              {designs.map((file, i) => (
                <div key={i} data-testid={`generate-case-slot-${i + 1}`}>
                  <ImageSlot slot="design" file={file}
                    removeLabel={designs.length > 1 ? `Remove case reference ${i + 1}` : "Remove case reference"}
                    onRemove={() => removeDesign(i)} />
                </div>
              ))}
              {designs.length < MAX_CASE_REFERENCES && (
                <div data-testid={`generate-case-slot-${designs.length + 1}`}>
                  <ImageSlot slot="design"
                    title={designs.length ? `Add case reference ${designs.length + 1}` : "Upload the case reference"}
                    onAdd={() => void pickImage(addDesign)} />
                </div>
              )}
            </div>
            <div className="field generate-panel__design">
              <span className="label">
                Product reference{" "}
                <InfoHint text="Optional, recommended — a photo of the real phone. It keeps the camera and colour exact." />
              </span>
              {product ? (
                <ImageSlot slot="product" file={product} removeLabel="Remove product reference"
                  onRemove={() => setProduct(undefined)} />
              ) : (
                <ImageSlot slot="product" title="Upload the product reference" onAdd={() => void pickImage(setProduct)} />
              )}
            </div>
            {/* Brand and model gate Generate: the prompt names the real phone. */}
            <fieldset className="generate-panel__details" data-testid="generate-details">
              <legend className="label">
                Details{" "}
                <InfoHint text="Brand and model name the real phone the image is grounded in. The SKU prefix labels templates and designs made from it." />
              </legend>
              <div className="generate-panel__details-grid">
                <ProductPickers idPrefix="generate" categoryTestId="generate-product-type"
                  modelTestId="generate-model" retryTestId="generate-model-retry"
                  categories={productTypes.map((t) => ({ value: t.code, label: t.label }))}
                  category={details.productType}
                  onCategory={chooseProductType}
                  brand={details.brand} onBrand={chooseBrand} brandOptions={catalog.brands} allowAddBrand={admin}
                  modelId={details.modelId} onModel={chooseModel} models={catalog.optionsFor(details.brand)}
                  modelsError={catalog.error} onRetryModels={() => void catalog.reload()}
                  onAddModel={admin ? addModel : undefined} />
                <StylePicker value={details.caseStyleId} testIdBase="generate" allowAdd={admin}
                  onChange={(caseStyleId) => setDetails((d) => ({ ...d, caseStyleId }))} onError={setError} />
                <BaseTypePicker value={details.baseType} testIdBase="generate" allowAdd={admin}
                  onChange={(baseType) => setDetails((d) => ({ ...d, baseType }))} />
                <label className="field">
                  <span className="label">SKU prefix</span>
                  <input
                    className="input"
                    type="text"
                    value={details.skuPrefix}
                    onChange={(e) => setDetails((d) => ({ ...d, skuPrefix: e.target.value }))}
                    placeholder="e.g. CASE-PRO"
                    data-testid="generate-sku-prefix"
                  />
                </label>
              </div>
            </fieldset>

            {offersMockupOnly(details.productType) && (
              <div className="field">
                <label className="label" htmlFor="generate-output">
                  Output{" "}
                  <InfoHint text="Mock-up only makes the clear mock-up and stops: no print guide and no template. The images collect on Only mock-ups." />
                </label>
                <select id="generate-output" className="input" value={output}
                  onChange={(e) => setOutput(e.target.value as GenerationOutput)} data-testid="generate-output">
                  <option value="print">Mock-up with print</option>
                  <option value="mockup">Mock-up only</option>
                </select>
              </div>
            )}

            {/* Views axis (multi-select) — each selected view = one queued job. */}
            {axisGroup && (
              <fieldset className="field generate-panel__group" data-testid="group-axis">
                <legend className="label">
                  {axisGroup.label}
                  <span className="faint" data-testid="axis-cap-hint">
                    {" · up to "}
                    {maxViews ?? 3}
                  </span>
                </legend>
                <ViewPicker
                  views={axisGroup.choices.map((c) => ({ view: c.id, label: c.label, thumb: c.reference_thumb ?? undefined }))}
                  selected={selectedViewIds}
                  max={maxViews ?? Infinity}
                  onChange={(next) => setSelected((prev) => ({ ...prev, [axisGroup.key]: next }))}
                />
              </fieldset>
            )}

            {/* Modifier groups — single → <select>, multi → pill chips. NO prompt. */}
            {modifierGroups.length > 0 && (
              <div className="generate-panel__modifiers">
                {modifierGroups.map((g) =>
                  g.select === "single" ? (
                    <div className="field generate-panel__group" key={g.key} data-testid={`group-${g.key}`}>
                      <label className="label" htmlFor={`modifier-${g.key}`}>
                        {g.label}
                      </label>
                      <select
                        id={`modifier-${g.key}`}
                        className="input"
                        value={selected[g.key]?.[0] ?? ""}
                        onChange={(e) => setSingle(g.key, e.target.value)}
                        data-testid={`modifier-select-${g.key}`}
                      >
                        <option value="">— None —</option>
                        {g.choices.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <fieldset className="field generate-panel__group" key={g.key} data-testid={`group-${g.key}`}>
                      <legend className="label">{g.label}</legend>
                      <div className="generate-panel__pills">
                        {g.choices.map((c) => {
                          const checked = (selected[g.key] ?? []).includes(c.id);
                          return (
                            <label
                              key={c.id}
                              className={`chip view-pill${checked ? " chip--on is-selected" : ""}`}
                              data-testid={`modifier-choice-${g.key}-${c.id}`}
                            >
                              <input
                                type="checkbox"
                                className="view-pill__input"
                                checked={checked}
                                onChange={() => toggleMulti(g.key, c.id)}
                                data-testid={`modifier-input-${g.key}-${c.id}`}
                              />
                              <span className="view-pill__check" aria-hidden="true">
                                {checked ? <Icon name="check" size={16}/> : <Icon name="plus" size={16}/>}
                              </span>
                              <span className="view-pill__label">{c.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>
                  ),
                )}
              </div>
            )}

          </div>
        </section>

        {/* The live results column. Every enqueued view is a row here, polled to completion;
            before the first run it is the one empty state, not a blank half-page. */}
        <section className="inspector generate-panel__results" aria-label="Generations">
          <header className="inspector__head">
            Generations
            {liveJobs.length > 0 && (
              <span className="caption" data-testid="generate-results-counts">{runCountsText(liveCounts)}</span>
            )}
          </header>
          <div className="inspector__body">
            {pollBanner}
            {openLiveRun ? gallery(openLiveRun, liveJobs, false) : liveJobs.length === 0 ? (
              <EmptyState
                icon="image"
                size="sm"
                title="No views generated yet"
                hint="Upload the case reference, name the phone, pick your views, then Generate."
                testId="generate-empty"
              />
            ) : (
            <ul className="generate-panel__job-rows" data-testid="job-status-list">
              {liveRuns.map((run, r) => (
                <RunRow key={run.runId} run={run} i={r} onOpen={openRun} autoFocus={run.runId === backFrom}
                  onRemove={(run) => void removeRuns([run])} busy={runBusy(run)} />
              ))}
            </ul>
            )}
          </div>
        </section>
      </div>

      {/* Rule 7 — the page's commit, and its ONE primary, in the bar at the bottom of the
          column. The step indicator rides in it, so "where am I" and "what happens next" sit
          on the same line instead of at opposite ends of the page. */}
      <ActionBar label="Generate actions" primary={
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => void generate()}
          disabled={!canGenerate}
          data-testid="generate-button"
        >
          {busy
            ? "Queuing…"
            : selectedViewIds.length > 1
              ? `Generate ${selectedViewIds.length} views`
              : "Generate"}
        </button>
      }>
        <GenerateSteps current={0} />
        {design && !productNamed && (
          <span className="caption" data-testid="generate-missing">Add the brand and model to generate.</span>
        )}
        <button type="button" className="btn" onClick={() => setShowSetup(false)} data-testid="generate-back-to-index">
          Back to generations
        </button>
        {openLiveRun ? continueOpenRun(openLiveRun) : (
          continueButton(() => nextLiveRun && onContinue(nextLiveRun), Boolean(nextLiveRun),
            "Place your design on this generation's ready views, in one template")
        )}
      </ActionBar>

      {previewModal}
    </section>
  );
}

function errorNotice(error: string | { detail: string; reference: string }) {
  return typeof error === "string" ? <ErrorNotice detail={error} /> : <ErrorNotice detail={error.detail} reference={error.reference} />;
}

function onlyUnsettledNote(run: (string | undefined)[], skipped: (string | undefined)[]): string {
  return `Only ${run.join(" and ")} ran, to finish the Higgsfield job already started. ` +
    `${skipped.join(" and ")} was not run; press Generate again to run it.`;
}

/**
 * The generate-module entry. AI Studio (create-once product templates) has been
 * removed; this renders the single-design generation flow directly. App.tsx mounts
 * this via the `generate` route.
 */
export function GeneratePanel({ onTemplateDone, onOpenTemplate, active }: GeneratePanelProps = {}) {
  return (
    <div className="generate-module" data-testid="generate-module">
      <SingleDesignPanel onTemplateDone={onTemplateDone} onOpenTemplate={onOpenTemplate} active={active} />
    </div>
  );
}
